import axiosInstance from "./axiosInstanceV1";
import { uploadTempImage } from "./cdnApi";

/**
 * Fetch albums with optional filtering parameters
 * @param {Object} params - Query parameters
 * @param {string} params.id - Specific album ID to fetch single album
 * @param {string} params.title - Filter by title
 * @param {boolean|number} params.random - Random ordering (boolean) or limit (number)
 * @param {number} params.limit - Maximum number of results
 * @param {boolean} params.includeMedia - Include media in response
 * @returns {Promise} API response
 */
export const fetchAlbums = async (params = {}) => {
	// Handle single album fetch if id is provided
	if (params.id) {
		const url = new URL(`/user/albums/${params.id}`, window.location.origin);

		// Add media parameter if requested
		if (params.includeMedia) {
			url.searchParams.append("media", "true");
		}

		const response = await axiosInstance.get(url.pathname + url.search);
		return response.data;
	}

	// Build query string for list queries
	const queryParams = new URLSearchParams();

	if (params.title) {
		queryParams.append("title", params.title);
	}

	if (params.random !== undefined) {
		// Handle random parameter - could be a number (for limit) or boolean
		if (typeof params.random === "number") {
			// If it's a number, pass it directly as the random parameter
			queryParams.append("random", params.random.toString());
		} else {
			// Otherwise use the value as is
			queryParams.append("random", params.random === null ? "" : params.random);
		}
	}

	if (params.limit) {
		queryParams.append("limit", params.limit);
	}

	// Add media parameter if requested
	if (params.includeMedia) {
		queryParams.append("media", "true");
	}

	// Create endpoint URL with query string
	const queryString = queryParams.toString();
	const endpoint = queryString ? `/user/albums?${queryString}` : "/user/albums";

	const response = await axiosInstance.get(endpoint);
	return response.data;
};

/**
 * Fetch sample albums for the public homepage.
 * Returns a hardcoded number of random albums with their media.
 * @returns {Promise} API response array of detailed albums
 */
export const fetchAlbumSamples = async () => {
	const response = await axiosInstance.get("/public/albums/samples");
	return response.data;
};

/**
 * Fetch a single album by ID
 * @param {string} albumId - Album ID
 * @param {boolean} includeMedia - Include media in response
 * @returns {Promise} API response
 */
export const fetchAlbum = async (albumId, includeMedia = false) => {
	return fetchAlbums({ id: albumId, includeMedia });
};

/**
 * Create a new album
 * @param {Object} albumData - Album data
 * @param {string} albumData.title - Album title
 * @param {string} albumData.description - Album description
 * @param {string} albumData.release_date - Release date
 * @returns {Promise} API response
 */
export const createAlbum = async (albumData) => {
	const response = await axiosInstance.post("/admin/albums", albumData, {
		headers: {
			"Content-Type": "application/json",
		},
	});
	return response.data;
};

/**
 * Upload a new album with cover image
 * @param {Object} albumData - Album data
 * @param {string} albumData.title - Album title
 * @param {string} albumData.description - Album description
 * @param {string} albumData.release_date - Release date
 * @param {File} [coverImageFile] - Optional cover image file; omit to create without a cover
 * @returns {Promise} API response
 */
export const uploadAlbum = async (
	albumData,
	coverImageFile,
	backgroundImageFile = null,
) => {
	const payload = {
		title: albumData.title,
		...(albumData.subtitle && { subtitle: albumData.subtitle }),
		...(albumData.image_prompt && { image_prompt: albumData.image_prompt }),
		...(albumData.description && { description: albumData.description }),
		...(albumData.release_date && { release_date: albumData.release_date }),
		...(albumData.tags && { tags: albumData.tags }),
	};

	// Both images are optional — only upload + reference a temp image when provided.
	if (coverImageFile) {
		payload.temp_cover_image_url = await uploadTempImage(coverImageFile);
	}
	if (backgroundImageFile) {
		payload.temp_background_image_url =
			await uploadTempImage(backgroundImageFile);
	}

	const response = await axiosInstance.post("/admin/albums/upload", payload, {
		headers: { "Content-Type": "application/json" },
	});

	return response.data;
};

/**
 * Update an existing album
 * @param {string} albumId - Album ID
 * @param {Object} albumData - Album data to update
 * @param {File} coverImageFile - Optional cover image file
 * @returns {Promise} API response
 */
export const updateAlbum = async (
	albumId,
	albumData,
	coverImageFile = null,
	backgroundImageFile = null,
) => {
	const payload = { ...albumData };

	// If new images are provided, upload to CDN __temp/ first
	if (coverImageFile) {
		payload.temp_cover_image_url = await uploadTempImage(coverImageFile);
	}
	if (backgroundImageFile) {
		payload.temp_background_image_url =
			await uploadTempImage(backgroundImageFile);
	}

	const response = await axiosInstance.put(
		`/admin/albums/${albumId}`,
		payload,
		{ headers: { "Content-Type": "application/json" } },
	);

	return response.data;
};

/**
 * Delete an album
 * @param {string} albumId - Album ID to delete
 * @returns {Promise} API response
 */
export const deleteAlbum = async (albumId) => {
	await axiosInstance.delete(`/admin/albums/${albumId}`);
	return true;
};

/**
 * Fetch media in an album
 * @param {string} albumId - Album ID
 * @returns {Promise} API response
 */
export const fetchAlbumMedia = async (albumId) => {
	const response = await axiosInstance.get(`/user/media?album=${albumId}`);
	return response.data;
};

/**
 * Update album cover image
 * @param {string} albumId - Album ID
 * @param {string} imageUrl - New cover image URL
 * @returns {Promise} API response
 */
export const updateCoverImage = async (albumId, imageUrl) => {
	const response = await axiosInstance.patch(
		`/admin/albums/${albumId}`,
		{
			cover_image_url: imageUrl,
		},
		{
			headers: {
				"Content-Type": "application/json",
			},
		},
	);

	return response.data;
};
